import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
  TextField,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@mui/material";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
  PieChart,
  Pie,
  Legend
} from "recharts";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";

import ProjectsOverviewReport from "../components/reports/ProjectsOverviewReport";
import CapacityOverviewReport from "../components/reports/CapacityOverviewReport";
import BoardOverviewReport from "../components/reports/BoardOverviewReport"; // eslint-disable-line -- kept for future use
import EmployeeNameMappingSyncDialog from "../components/reports/EmployeeNameMappingSyncDialog";

import AppSelect from "../ui/AppSelect";
import AppButton from "../ui/AppButton";
import DataTable from "../ui/Table";
import {
  getProjectsOverview,
  getCapacityOverview,
  getBoardOverview,
  getExpiringAssignments,
  getWeeklyUtilization,
  getEmployeeNameMappings,
  uploadEmployeeNameMappingFile
} from "../services/reportService";
import { useDepartments } from "../context/DepartmentsContext";
import { initialsOf } from "../utils/commentUtils";
import { avatarColorOf } from "../utils/avatarUtils";
import { getToken } from "../config/msalConfig";
import uiConfig from "../config/uiConfig";

const PALETTE = uiConfig.chartPalette;

function KpiCard({ icon, label, value, sub, color = "#2563eb", bg = "#eff6ff" }) {
  return (
    <Paper
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* coloured top accent bar */}
      <Box sx={{ height: 5, bgcolor: color, flexShrink: 0 }} />

      <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 2.5, flex: 1 }}>
        {/* icon blob */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
        </Box>

        {/* text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", mb: 0.3, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 38, fontWeight: 800, color: "text.primary", lineHeight: 1 }}>
            {value ?? "—"}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: 11.5, color: "text.disabled", mt: 0.6 }}>{sub}</Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

const capColor = (v) => {
  if (v > 100) return "#ef4444";
  if (v >= 76) return "#7c3aed";
  if (v >= 51) return "#2563eb";
  if (v >= 26) return "#16a34a";
  return "#94a3b8";
};

function Reports() {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [board, setBoard] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [weeklyUtil, setWeeklyUtil] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [mappingRows, setMappingRows] = useState([]);
  const [mappingSummary, setMappingSummary] = useState({ total: 0, matched: 0, unmatched: 0 });
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingSyncDialogOpen, setMappingSyncDialogOpen] = useState(false);
  const [mappingUploadLoading, setMappingUploadLoading] = useState(false);
  const [mappingSearch, setMappingSearch] = useState("");
  const [mappingOnlyUnmatched, setMappingOnlyUnmatched] = useState(false);
  const [mappingSelectedFile, setMappingSelectedFile] = useState(null);
  const [mappingSelectedFileName, setMappingSelectedFileName] = useState("");
  const [mappingFilePath, setMappingFilePath] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editingEmail, setEditingEmail] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyAllLoading, setNotifyAllLoading] = useState(false);
  const [mappingExpiresFilter, setMappingExpiresFilter] = useState("all");
  const fileInputRef = useRef(null);

  const PERIODS = [
    { key: "day",     label: "Day" },
    { key: "week",    label: "Week" },
    { key: "month",   label: "Month" },
    { key: "quarter", label: "Quarter" },
    { key: "half",    label: "Half-Year" },
    { key: "year",    label: "Year" },
  ];
  const [period, setPeriod] = useState("month");
  const [periodOffset, setPeriodOffset] = useState(0);

  const { startDate, endDate, periodLabel } = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear(); const m = now.getMonth();
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    if (period === "day") {
      const d = new Date(y, m, now.getDate() + periodOffset);
      return { startDate: fmt(d), endDate: fmt(d), periodLabel: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) };
    }
    if (period === "week") {
      const mon = new Date(y, m, now.getDate() - now.getDay() + 1 + periodOffset * 7);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return { startDate: fmt(mon), endDate: fmt(sun), periodLabel: `${fmt(mon)} – ${fmt(sun)}` };
    }
    if (period === "month") {
      const mo = ((m + periodOffset) % 12 + 12) % 12;
      const yr = y + Math.floor((m + periodOffset) / 12);
      const s = new Date(yr, mo, 1); const e = new Date(yr, mo + 1, 0);
      return { startDate: fmt(s), endDate: fmt(e), periodLabel: `${monthNames[mo]} ${yr}` };
    }
    if (period === "quarter") {
      const q = Math.floor(m / 3) + periodOffset;
      const qAdj = ((q % 4) + 4) % 4; const yr = y + Math.floor(q / 4);
      const s = new Date(yr, qAdj * 3, 1); const e = new Date(yr, qAdj * 3 + 3, 0);
      return { startDate: fmt(s), endDate: fmt(e), periodLabel: `Q${qAdj+1} ${yr}` };
    }
    if (period === "half") {
      const h = (m < 6 ? 0 : 1) + periodOffset;
      const hAdj = ((h % 2) + 2) % 2; const yr = y + Math.floor(h / 2);
      const s = new Date(yr, hAdj * 6, 1); const e = new Date(yr, hAdj * 6 + 6, 0);
      return { startDate: fmt(s), endDate: fmt(e), periodLabel: `H${hAdj+1} ${yr}` };
    }
    // year
    const yr = y + periodOffset;
    return { startDate: `${yr}-01-01`, endDate: `${yr}-12-31`, periodLabel: String(yr) };
  }, [period, periodOffset]);

  const { departments } = useDepartments();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);

    Promise.all([
      getProjectsOverview(departmentFilter || null, startDate, endDate),
      getCapacityOverview(departmentFilter || null, startDate, endDate),
      getBoardOverview(departmentFilter || null),
      getExpiringAssignments(departmentFilter || null, startDate, endDate),
      getWeeklyUtilization(departmentFilter || null, startDate, endDate)
    ])
      .then(([p, c, b, e, w]) => { setProjects(p); setCapacity(c); setBoard(b); setExpiring(e); setWeeklyUtil(w); })
      .catch((error) => console.error("Reports Error:", error))
      .finally(() => setLoading(false));
  }, [departmentFilter, startDate, endDate]);

  const filterMappingsByExpires = (rows, filterType) => {
    if (filterType === "all") return rows;
    return rows.filter((row) => {
      const days = row.days_until_end_date;
      if (typeof days !== "number") return false;
      if (filterType === "expired") return days < 0;
      if (filterType === "days") return days >= 0 && days < 7;
      if (filterType === "weeks") return days >= 7 && days < 30;
      if (filterType === "months") return days >= 30 && days < 90;
      if (filterType === "quarter") return days >= 90 && days < 180;
      if (filterType === "half") return days >= 180 && days < 365;
      if (filterType === "year") return days >= 365;
      return true;
    });
  };

  const loadNameMappings = async (search = "", onlyUnmatched = false, expiresFilter = "all") => {
    setMappingLoading(true);
    try {
      const response = await getEmployeeNameMappings(search, onlyUnmatched);
      const filtered = filterMappingsByExpires(response?.rows || [], expiresFilter);
      setMappingRows(filtered);
      setMappingSummary(response?.summary || { total: 0, matched: 0, unmatched: 0 });
    } catch (error) {
      console.error("Name Mapping Error:", error);
      setMappingRows([]);
      setMappingSummary({ total: 0, matched: 0, unmatched: 0 });
    } finally {
      setMappingLoading(false);
    }
  };

  const handleEditEmail = (row) => {
    setEditingRow(row);
    setEditingEmail(row.matched_employee_email || "");
    setEditDialogOpen(true);
  };

  const handleSaveEmail = async () => {
    if (!editingRow || !editingEmail.trim()) return;
    try {
      setMappingRows((prev) =>
        prev.map((r) =>
          r.mapping_id === editingRow.mapping_id
            ? { ...r, matched_employee_email: editingEmail.trim() }
            : r
        )
      );
      setEditDialogOpen(false);
      // TODO: Call API to persist email update
    } catch (error) {
      console.error("Error updating email:", error);
    }
  };

  const handleNotifyUser = async (row) => {
    setNotifyLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}/api/reports/employee-name-mappings/${row.mapping_id}/notify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "oidc-token": token,
        },
        body: JSON.stringify({ email: row.matched_employee_email }),
      });
      if (response.ok) {
        alert("Notification sent successfully");
      } else {
        alert("Failed to send notification");
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      alert("Error sending notification");
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleNotifyAll = async () => {
    const rowsToNotify = mappingRows.filter((row) => row.matched_employee_email);
    
    if (rowsToNotify.length === 0) {
      alert("No rows with email addresses to notify");
      return;
    }

    const confirmNotify = window.confirm(
      `Send notifications to ${rowsToNotify.length} matching row(s) based on current filter? This action cannot be undone.`
    );
    
    if (!confirmNotify) return;

    setNotifyAllLoading(true);
    let successCount = 0;
    let failureCount = 0;

    try {
      const token = await getToken();

      for (const row of rowsToNotify) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}/api/reports/employee-name-mappings/${row.mapping_id}/notify`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "oidc-token": token,
            },
            body: JSON.stringify({ email: row.matched_employee_email }),
          });
          
          if (response.ok) {
            successCount++;
          } else {
            failureCount++;
          }
        } catch (error) {
          console.error(`Error notifying ${row.source_name}:`, error);
          failureCount++;
        }
      }

      const message = `Notifications sent: ${successCount} successful${failureCount > 0 ? `, ${failureCount} failed` : ""}`;
      alert(message);
    } catch (error) {
      console.error("Error in bulk notification:", error);
      alert("Error sending bulk notifications");
    } finally {
      setNotifyAllLoading(false);
    }
  };

  useEffect(() => {
    loadNameMappings(mappingSearch, mappingOnlyUnmatched, mappingExpiresFilter);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => {
      loadNameMappings(mappingSearch, mappingOnlyUnmatched, mappingExpiresFilter);
    }, 300);
    return () => clearTimeout(timer);
  }, [mappingSearch, mappingOnlyUnmatched, mappingExpiresFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setMappingSelectedFile(file);
    setMappingSelectedFileName(file?.name || "");
    setMappingFilePath("");
  };

  const handleSyncNameMappings = async () => {
    if (!mappingSelectedFile) return;

    setMappingUploadLoading(true);
    try {
      const uploaded = await uploadEmployeeNameMappingFile(mappingSelectedFile);
      setMappingFilePath(uploaded?.file_path || "");
      setMappingSyncDialogOpen(true);
    } catch (error) {
      console.error("Name Mapping Upload Error:", error);
    } finally {
      setMappingUploadLoading(false);
    }
  };

  const activeProjects = projects?.by_status?.find((s) => s.name === "Active")?.value ?? 0;
  const overAllocated = capacity?.over_allocated_count ?? (capacity?.distribution?.find((d) => d.name === "Over 100%")?.value ?? 0);
  const fullyAllocated = (capacity?.distribution?.find((d) => d.name === "76-100%")?.value ?? 0) + overAllocated;
  const benchCount = capacity?.bench_count ?? 0;

  // Merge top_allocated + bench into one allocation table (dedup by employee_id)
  const allEmployeeRows = React.useMemo(() => {
    const seen = new Set();
    const rows = [];
    for (const emp of [...(capacity?.top_allocated || []), ...(capacity?.bench || [])]) {
      if (!seen.has(emp.employee_id)) { seen.add(emp.employee_id); rows.push(emp); }
    }
    return rows.sort((a, b) => b.total_capacity_percent - a.total_capacity_percent);
  }, [capacity]);

  const marIdExpireLabel = React.useMemo(() => {
    const filterLabels = {
      all: "All",
      expired: "Expired",
      days: "Days",
      weeks: "Weeks",
      months: "Months",
      quarter: "Quarter",
      half: "6 Months",
      year: "Year",
    };
    return filterLabels[mappingExpiresFilter] || "All";
  }, [mappingExpiresFilter]);

  const kpiCards = [
    { icon: <WorkOutlineOutlinedIcon />, label: "Total Projects", value: projects?.total_projects, color: "#4338ca", bg: "#eef2ff",
      sub: `${activeProjects} active · ${projects?.by_status?.find((s) => s.name === "Upcoming")?.value ?? 0} upcoming` },
    { icon: <PeopleAltOutlinedIcon />, label: "Fully Allocated", value: fullyAllocated, color: "#2563eb", bg: "#eff6ff", sub: "employees at 76–100%+" },
    { icon: <WarningAmberRoundedIcon />, label: "Over-Allocated", value: overAllocated, color: "#dc2626", bg: "#fee2e2", sub: "exceed their default capacity" },
  ];

  const handleExportCsv = () => {
    const rows = [
      ["Metric", "Value"],
      ["Total Projects", projects?.total_projects ?? ""],
      ...(projects?.by_status || []).map((r) => [`Projects — ${r.name}`, r.value]),
      ...(projects?.by_type || []).map((r) => [`Project Type — ${r.name}`, r.value]),
      ["Employees On Bench", benchCount],
      ["Over-Allocated Employees", overAllocated],
      ...(capacity?.distribution || []).map((r) => [`Capacity Bucket — ${r.name}`, r.value]),
      ...(allEmployeeRows).map((r) => [`Employee — ${r.employee_name}`, `${r.total_capacity_percent}% (${r.project_count} projects)`]),
    ];
    const csvContent = rows.map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `resource-management-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh", p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Skeleton variant="text" width={280} height={40} />
            <Skeleton variant="text" width={360} height={20} />
          </Box>
          <Box display="flex" gap={1.5}>
            <Skeleton variant="rounded" width={200} height={36} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={320} height={36} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={130} height={36} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>

        {/* 3 KPI cards */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(3,1fr)" }, gap: 2.5, mb: 3 }}>
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: 3 }} />)}
        </Box>

        {/* Projects overview — 2 charts side by side */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
        </Box>

        {/* Capacity overview — full width */}
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3, mb: 3 }} />

        {/* Utilization bar + donut */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2, mb: 3 }}>
          <Skeleton variant="rounded" height={340} sx={{ borderRadius: 3 }} />
          <Skeleton variant="rounded" height={340} sx={{ borderRadius: 3 }} />
        </Box>

        {/* Weekly utilization */}
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3, mb: 3 }} />

        {/* Expiring assignments */}
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", p: 3 }}>

      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} color="text.primary">Resource Management Reports</Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Allocation, capacity and project status analytics across your teams
          </Typography>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
          <Box sx={{ minWidth: 220 }}>
            <AppSelect
              placeholder="All departments"
              isClearable
              options={departments.map((d) => ({ value: d.department_id, label: d.department_name }))}
              value={departmentFilter || null}
              onChange={(value) => setDepartmentFilter(value || "")}
            />
          </Box>

          {/* Period filter */}
          <Box display="flex" alignItems="center" gap={0.5} sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, px: 1, py: 0.5 }}>
            {PERIODS.map((p) => (
              <Box
                key={p.key}
                onClick={() => { setPeriod(p.key); setPeriodOffset(0); }}
                sx={{
                  px: 1.2, py: 0.4, borderRadius: 1.5, cursor: "pointer", fontSize: 12, fontWeight: period === p.key ? 700 : 500,
                  bgcolor: period === p.key ? "primary.main" : "transparent",
                  color: period === p.key ? "#fff" : "text.secondary",
                  "&:hover": { bgcolor: period === p.key ? "primary.dark" : "action.hover" },
                  transition: "all 0.15s",
                }}
              >
                {p.label}
              </Box>
            ))}
          </Box>

          <AppButton variant="outlined" startIcon={<FileDownloadOutlinedIcon fontSize="small" />} onClick={handleExportCsv} sx={{ height: "auto" }}>
            Export CSV
          </AppButton>
        </Box>
      </Box>

      {/* KPI CARDS */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(3,1fr)" }, gap: 2.5, mb: 3 }}>
        {kpiCards.map((card) => <KpiCard key={card.label} {...card} />)}
      </Box>

      {/* Resource KPI cards — Total Resources Capacity + Total Utilization + On Bench */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(3,1fr)" }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>Total Resources Capacity</Typography>
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography sx={{ fontSize: 36, fontWeight: 800, color: "text.primary", lineHeight: 1 }}>
              {capacity?.total_default_capacity_fte ?? (allEmployeeRows.reduce((s, e) => s + (e.default_capacity_percent ?? 100), 0) / 100).toFixed(1)}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: "text.secondary" }}>FTE</Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
            {allEmployeeRows.length} employee(s) · {allEmployeeRows.reduce((s, e) => s + (e.total_capacity_percent || 0), 0)}% total allocated
          </Typography>
        </Paper>

        <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>Total Utilization</Typography>
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography sx={{ fontSize: 36, fontWeight: 800, color: "text.primary", lineHeight: 1 }}>
              {allEmployeeRows.filter((e) => (e.total_capacity_percent || 0) > 0).length}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              of {allEmployeeRows.length} utilized
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {allEmployeeRows.filter((e) => (e.total_capacity_percent || 0) === 0).length} with no active allocation
          </Typography>
        </Paper>

        <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>On Bench</Typography>
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography sx={{ fontSize: 36, fontWeight: 800, color: "text.primary", lineHeight: 1 }}>
              {benchCount}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              employee{benchCount !== 1 ? "s" : ""}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            under 50% allocation
          </Typography>
        </Paper>
      </Box>

      {/* ROW 1: Projects Overview */}
      <Box sx={{ mb: 3 }}>
        <ProjectsOverviewReport departmentId={departmentFilter || null} startDate={startDate} endDate={endDate} />
      </Box>

      {/* ROW 1b: Capacity & Resource Overview — full width */}
      <Box sx={{ mb: 3 }}>
        <CapacityOverviewReport departmentId={departmentFilter || null} startDate={startDate} endDate={endDate} />
      </Box>

      {/* ROW 2: Employee Capacity Utilization (horizontal bar) + Allocation donut */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2, mb: 3 }}>

        {/* Horizontal capacity bar chart */}
        <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Employee Capacity Utilization</Typography>
            <Chip size="small" label={`Top 10 of ${allEmployeeRows.length} employees`} sx={{ fontWeight: 700, background: "#eef2ff", color: "#4338ca" }} />
          </Box>
          {allEmployeeRows.length === 0 ? (
            <Typography color="text.secondary" fontSize={13}>No employee data</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, Math.min(allEmployeeRows.length, 10) * 34)}>
              <BarChart data={allEmployeeRows.slice(0, 10)} layout="vertical" margin={{ left: 10, right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" domain={[0, 120]} tickFormatter={(v) => `${v}%`} fontSize={11} />
                <YAxis type="category" dataKey="employee_name" fontSize={11} width={110} />
                <Tooltip formatter={(v) => [`${v}%`, "Capacity"]} />
                <Bar dataKey="total_capacity_percent" radius={[0, 6, 6, 0]} maxBarSize={18} label={{ position: "right", fontSize: 10, formatter: (v) => `${v}%` }}>
                  {allEmployeeRows.slice(0, 10).map((emp) => (
                    <Cell key={emp.employee_id} fill={capColor(emp.total_capacity_percent)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>

        {/* Allocation distribution donut */}
        <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>Allocation Distribution</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>Employees by capacity bucket</Typography>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={capacity?.distribution || []}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={78}
              >
                {(capacity?.distribution || []).map((entry, i) => (
                  <Cell key={entry.name} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
          <Divider sx={{ my: 1.5 }} />
          {(capacity?.distribution || []).map((d, i) => (
            <Box key={d.name} display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.4 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: PALETTE[i % PALETTE.length] }} />
                <Typography fontSize={12} color="text.secondary">{d.name}</Typography>
              </Box>
              <Typography fontSize={12} fontWeight={700}>{d.value}</Typography>
            </Box>
          ))}
        </Paper>
      </Box>

      {/* ROW 3: Weekly Team Utilization Calendar */}
      {weeklyUtil.length > 0 && (() => {
        const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
        const cellColor = (avg) => {
          if (avg >= 100) return { bg: "#fee2e2", text: "#dc2626", border: "#fca5a5" };
          if (avg >= 76)  return { bg: "#ede9fe", text: "#7c3aed", border: "#c4b5fd" };
          if (avg >= 51)  return { bg: "#dbeafe", text: "#2563eb", border: "#93c5fd" };
          if (avg >= 26)  return { bg: "#dcfce7", text: "#16a34a", border: "#86efac" };
          return { bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1" };
        };
        const today = new Date(); today.setHours(0,0,0,0);
        return (
          <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", mb: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Team Utilization — Next 12 Weeks</Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Average capacity % across all employees per week</Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                {[{ label: "0-25%", bg: "#f1f5f9", text: "#64748b" }, { label: "26-50%", bg: "#dcfce7", text: "#16a34a" }, { label: "51-75%", bg: "#dbeafe", text: "#2563eb" }, { label: "76-100%", bg: "#ede9fe", text: "#7c3aed" }, { label: "100%+", bg: "#fee2e2", text: "#dc2626" }].map((l) => (
                  <Chip key={l.label} size="small" label={l.label} sx={{ bgcolor: l.bg, color: l.text, fontWeight: 600, fontSize: 10, height: 20 }} />
                ))}
              </Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.5 }}>
              {weeklyUtil.map((wk) => {
                const c = cellColor(wk.avg_capacity);
                const wkDate = new Date(wk.week); wkDate.setHours(0,0,0,0);
                const isCurrent = wkDate <= today && today <= new Date(wk.week_end);
                return (
                  <Box key={wk.week} sx={{ p: 1.5, borderRadius: 2, bgcolor: c.bg, border: `1px solid ${isCurrent ? c.text : c.border}`, outline: isCurrent ? `2px solid ${c.text}` : "none", outlineOffset: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary" }}>
                        {fmtDate(wk.week)} – {fmtDate(wk.week_end)}
                      </Typography>
                      {isCurrent && <Chip label="Now" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: c.text, color: "#fff" }} />}
                    </Box>
                    <Typography sx={{ fontSize: 28, fontWeight: 800, color: c.text, lineHeight: 1 }}>{wk.avg_capacity}%</Typography>
                    <Typography sx={{ fontSize: 10, color: "text.disabled", mt: 0.4 }}>{wk.employee_count} employee{wk.employee_count !== 1 ? "s" : ""} assigned</Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        );
      })()}

      {/* ROW 3b: Over-Allocated Employees */}
      {(() => {
        const overList = allEmployeeRows.filter(
          (e) => (e.total_capacity_percent || 0) > (e.default_capacity_percent ?? 100)
        );
        if (overList.length === 0) return null;
        return (
          <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", mb: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <WarningAmberRoundedIcon sx={{ color: "#dc2626", fontSize: 22 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>Over-Allocated Employees</Typography>
              </Box>
              <Chip size="small" label={`${overList.length} employee(s)`} sx={{ fontWeight: 700, bgcolor: "#fee2e2", color: "#dc2626" }} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {overList.map((emp) => (
                <Box key={emp.employee_id} display="flex" alignItems="center" justifyContent="space-between"
                  sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "#fecaca", bgcolor: "#fff5f5" }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: "#dc2626" }}>
                      {emp.employee_name?.charAt(0)}
                    </Avatar>
                    <Typography fontSize={13} fontWeight={600}>{emp.employee_name}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Chip size="small" label={`Default: ${emp.default_capacity_percent ?? 100}%`}
                      sx={{ fontSize: 11, fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569" }} />
                    <Chip size="small" label={`Allocated: ${emp.total_capacity_percent}%`}
                      sx={{ fontSize: 11, fontWeight: 700, bgcolor: "#fee2e2", color: "#dc2626" }} />
                    <Chip size="small" label={`+${emp.total_capacity_percent - (emp.default_capacity_percent ?? 100)}%`}
                      sx={{ fontSize: 11, fontWeight: 700, bgcolor: "#dc2626", color: "#fff" }} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        );
      })()}

      {/* ROW 4: Resources Expiring Soon */}
      {expiring && (
        <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
            <EventBusyRoundedIcon sx={{ color: "#dc2626", fontSize: 22 }} />
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>Resources Expiring Soon</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Assignments ending within the next 14 days</Typography>
            </Box>
          </Box>

          {[
            { key: "today",   label: "Ending Today",        color: "#dc2626", bg: "#fee2e2", borderColor: "#fca5a5", icon: <TodayRoundedIcon sx={{ fontSize: 16 }} /> },
            { key: "3_days",  label: "Within 3 Days",       color: "#c2410c", bg: "#fff7ed", borderColor: "#fdba74", icon: <EventBusyRoundedIcon sx={{ fontSize: 16 }} /> },
            { key: "7_days",  label: "Within 7 Days",       color: "#b45309", bg: "#fef3c7", borderColor: "#fcd34d", icon: <ScheduleRoundedIcon sx={{ fontSize: 16 }} /> },
            { key: "14_days", label: "Within 14 Days",      color: "#1d4ed8", bg: "#eff6ff", borderColor: "#93c5fd", icon: <ScheduleRoundedIcon sx={{ fontSize: 16 }} /> },
          ].map(({ key, label, color, bg, borderColor, icon }) => {
            const rows = expiring[key] || [];
            if (rows.length === 0) return null;
            return (
              <Box key={key} sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Box sx={{ color, display: "flex" }}>{icon}</Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color }}>{label}</Typography>
                  <Chip size="small" label={rows.length} sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: bg, color, border: `1px solid ${borderColor}` }} />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(3,1fr)", lg: "repeat(4,1fr)" }, gap: 1.5 }}>
                  {rows.map((row, i) => (
                    <Box key={`${row.employee_id}-${row.project_id}-${i}`}
                      onClick={() => navigate(`/resource-management?employee=${row.employee_id}`)}
                      sx={{ p: 1.5, borderRadius: 2, bgcolor: bg, border: `1px solid ${borderColor}`, cursor: "pointer",
                        "&:hover": { filter: "brightness(0.95)", transform: "translateY(-1px)", transition: "all 0.15s" } }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary" }}>{row.employee_name}</Typography>
                        <Chip size="small"
                          label={row.days_left === 0 ? "Today" : `${row.days_left}d left`}
                          sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: color, color: "#fff" }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: 11, color: "text.secondary" }} noWrap>{row.project_name}</Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                        <Typography sx={{ fontSize: 10, color: "text.disabled" }}>{row.end_date}</Typography>
                        <Chip size="small" label={`${row.capacity_percent}%`}
                          sx={{ height: 16, fontSize: 10, fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569" }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}

          {Object.values(expiring).every((arr) => arr.length === 0) && (
            <Typography color="text.secondary" fontSize={13}>No assignments expiring within the next 14 days</Typography>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={2}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>External Employee Name Mapping</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Map sheet names like "Rout, Nabasundar" to your employee master list</Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Chip size="small" label={`Total: ${mappingSummary.total || 0}`} sx={{ fontWeight: 700, bgcolor: "#f1f5f9", color: "#334155" }} />
            <Chip size="small" label={`Matched: ${mappingSummary.matched || 0}`} sx={{ fontWeight: 700, bgcolor: "#ecfeff", color: "#0e7490" }} />
            <Chip size="small" label={`Unmatched: ${mappingSummary.unmatched || 0}`} sx={{ fontWeight: 700, bgcolor: "#fff7ed", color: "#c2410c" }} />
          </Box>
        </Box>

        <Box display="flex" gap={1.5} flexWrap="wrap" mb={1.5}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          <AppButton variant="outlined" startIcon={<UploadFileRoundedIcon fontSize="small" />} onClick={handleSelectFile}>
            Choose File
          </AppButton>

          <Box sx={{ flex: 1, minWidth: 260, display: "flex", alignItems: "center" }}>
            <Typography fontSize={12.5} color={mappingSelectedFileName ? "text.primary" : "text.secondary"}>
              {mappingSelectedFileName || "No file selected"}
            </Typography>
          </Box>

          <AppButton onClick={handleSyncNameMappings} disabled={!mappingSelectedFile || mappingUploadLoading}>
            Sync Mapping
          </AppButton>

          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleNotifyAll}
            disabled={notifyAllLoading || mappingRows.length === 0}
            sx={{
              bgcolor: "#2563eb",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#1d4ed8" },
              "&:disabled": { bgcolor: "#cbd5e1", color: "#78716c" },
            }}
          >
            {notifyAllLoading ? `Notifying... (${mappingRows.filter(r => r.matched_employee_email).length})` : `Notify All (${mappingRows.filter(r => r.matched_employee_email).length})`}
          </Button>
        </Box>

        {mappingUploadLoading && <LinearProgress sx={{ mb: 2 }} />}

        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap" mb={2}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <TextField
              fullWidth
              size="small"
              label="Search name"
              value={mappingSearch}
              onChange={(e) => setMappingSearch(e.target.value)}
            />
          </Box>
          <FormControlLabel
            control={<Switch size="small" checked={mappingOnlyUnmatched} onChange={(e) => setMappingOnlyUnmatched(e.target.checked)} />}
            label="Only unmatched"
            sx={{ "& .MuiFormControlLabel-label": { fontSize: 13, color: "text.secondary" } }}
          />

          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontSize={12} fontWeight={600} color="text.secondary" sx={{ whiteSpace: "nowrap" }}>Expires In:</Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {[
                { value: "all", label: "All" },
                { value: "expired", label: "Expired" },
                { value: "days", label: "Days" },
                { value: "weeks", label: "Weeks" },
                { value: "months", label: "Months" },
                { value: "quarter", label: "Quarter" },
                { value: "half", label: "6 Months" },
                { value: "year", label: "Year" },
              ].map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  onClick={() => setMappingExpiresFilter(opt.value)}
                  variant={mappingExpiresFilter === opt.value ? "filled" : "outlined"}
                  sx={{
                    fontWeight: mappingExpiresFilter === opt.value ? 700 : 500,
                    bgcolor: mappingExpiresFilter === opt.value ? "#4338ca" : "transparent",
                    color: mappingExpiresFilter === opt.value ? "#fff" : "#475569",
                    borderColor: mappingExpiresFilter === opt.value ? "transparent" : "#cbd5e1",
                    cursor: "pointer",
                    fontSize: 12,
                    "&:hover": {
                      bgcolor: mappingExpiresFilter === opt.value ? "#3730a3" : "#f1f5f9",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <DataTable
          columns={[
            {
              key: "source_name",
              label: "Contract Name",
              width: "25%",
              render: (row) => (
                <Box>
                  <Typography fontSize={13} fontWeight={600} color="text.primary">{row.source_name}</Typography>
                  <Typography fontSize={12} color="text.secondary">{row.matched_employee_email || "-"}</Typography>
                </Box>
              )
            },
            {
              key: "date_range",
              label: "Contract Period",
              width: "15%",
              render: (row) => (
                <Box>
                  <Typography fontSize={12} color="text.secondary"><strong>Start:</strong> {row.current_start_date || "-"}</Typography>
                  <Typography fontSize={12} color="text.secondary"><strong>End:</strong> {row.current_end_date || "-"}</Typography>
                </Box>
              )
            },
            {
              key: "expires_in",
              label: "Expires In",
              width: "15%",
              render: (row) => {
                const days = row.days_until_end_date;

                if (typeof days !== "number") {
                  return <Typography fontSize={13} color="text.secondary">-</Typography>;
                }

                if (days < 0) {
                  return (
                    <Chip
                      size="small"
                      label={`Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`}
                      sx={{ fontWeight: 700, bgcolor: "#fee2e2", color: "#dc2626" }}
                    />
                  );
                }

                const isUrgent = days <= 15;
                let label = "";

                if (days < 7) {
                  label = `${days} day${days === 1 ? "" : "s"}`;
                } else if (days < 30) {
                  const weeks = Math.ceil(days / 7);
                  label = `${weeks} week${weeks === 1 ? "" : "s"}`;
                } else {
                  const months = Math.ceil(days / 30);
                  label = `${months} month${months === 1 ? "" : "s"}`;
                }

                return (
                  <Chip
                    size="small"
                    label={label}
                    sx={{
                      fontWeight: 700,
                      bgcolor: isUrgent ? "#ffedd5" : "#eef2ff",
                      color: isUrgent ? "#c2410c" : "#4338ca",
                    }}
                  />
                );
              }
            },
            {
              key: "hiring_manager",
              label: "Hiring Manager",
              width: "12%",
              render: (row) => (
                <Typography fontSize={13} color="#0000a0" sx={{fontWeight:"600"}}>{row.hiring_manager || "-"}</Typography>
              )
            },
            {
              key: "is_matched",
              label: "Status",
              width: "12%",
              render: (row) => (
                <Chip
                  size="small"
                  label={row.is_matched ? "Matched" : "Unmatched"}
                  sx={{
                    fontWeight: 700,
                    bgcolor: row.is_matched ? "#ecfeff" : "#fff7ed",
                    color: row.is_matched ? "#0e7490" : "#c2410c",
                  }}
                />
              )
            },
            {
              key: "actions",
              label: "Actions",
              width: "11%",
              render: (row) => (
                <Box display="flex" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditEmail(row)}
                    sx={{ color: "#4338ca" }}
                    title="Edit email"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleNotifyUser(row)}
                    disabled={!row.matched_employee_email || notifyLoading}
                    sx={{ color: "#2563eb" }}
                    title="Send notification"
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Box>
              )
            },
          ]}
          rows={mappingRows}
          loading={mappingLoading}
          tableLayout="fixed"
          rowKey={(row) => row.mapping_id}
          pagination={mappingRows.length > 10}
          rowsPerPage={10}
          emptyState={{ title: "No mapped rows found", subtitle: "Sync the external sheet to generate employee name mapping." }}
        />
      </Paper>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Email</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography fontSize={12} color="text.secondary" sx={{ mb: 2 }}>
            Contract Name: <strong>{editingRow?.source_name}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Email Address"
            value={editingEmail}
            onChange={(e) => setEditingEmail(e.target.value)}
            placeholder="user@example.com"
            type="email"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <AppButton variant="outlined" onClick={() => setEditDialogOpen(false)}>
            Cancel
          </AppButton>
          <AppButton variant="contained" onClick={handleSaveEmail}>
            Save
          </AppButton>
        </DialogActions>
      </Dialog>

      <EmployeeNameMappingSyncDialog
        open={mappingSyncDialogOpen}
        filePath={mappingFilePath}
        onClose={() => setMappingSyncDialogOpen(false)}
        onDone={() => loadNameMappings(mappingSearch, mappingOnlyUnmatched, mappingExpiresFilter)}
      />

    </Box>
  );
}

export default Reports;
